import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { ChartComponent } from 'ng-apexcharts';
import {TranslatePipe} from '@ngx-translate/core';
import { BaseLoadComponent } from '@shared/components/classes/base-load.component';
import { Resident } from '@modules/universal-billing/residents/residents.interfaces';
import { Observable } from 'rxjs';
import { ResidentsService } from '@modules/universal-billing/residents/residents.service';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { ModalClose } from '@shared/services/modal/modal.enum';
import { ModalService } from '@shared/services/modal/modal.service';
import { ProgressSpinnerModule } from '@primeng/progressspinner';
import { FullNamePipe } from '@common/pipe/full-name.pipe';
import { Resources } from '@app/resources';
import { UnitTypes } from '@shared/enums/UnitTypes.enum';
import { ButtonModule } from '@primeng/button';
import {
  ContractComponent
} from '@modules/universal-billing/residents/resident-profile/compoents/contract/contract.component';
import {
  ResidentPaymentsComponent
} from '@modules/universal-billing/residents/resident-profile/compoents/resident-payments/resident-payments.component';
import {
  ResidentInvoicesComponent
} from '@modules/universal-billing/residents/resident-profile/compoents/resident-invoices/resident-invoices.component';
import { trigger, transition, style, animate } from '@angular/animations';
import {NgxSkeletonLoaderComponent} from "ngx-skeleton-loader";
import {delay} from 'rxjs/operators';

interface ConnectedUnitType {
  type: string;
  billingPeriod: string;
  isActive: boolean;
}

interface Payment {
  paymentAmount: number;
  paymentDate: Date;
  paymentType: string;
  paymentStatus: string;
  paymentDetails?: string;
}

interface Invoice {
  invoiceId: string;
  invoiceDate: Date;
  amountDue: number;
  dueDate: Date;
  status: string;
}

@Component({
  selector: 'app-resident-profile',
  templateUrl: './resident-profile.component.html',
  styleUrls: ['./resident-profile.component.scss'],
    imports: [
        NgForOf,
        DatePipe,
        NgClass,
        NgIf,
        ChartComponent,
        TranslatePipe,
        PageTitleComponent,
        ProgressSpinnerModule,
        FullNamePipe,
        ButtonModule,
        ResidentPaymentsComponent,
        ResidentInvoicesComponent,
        CommonModule,
        NgxSkeletonLoaderComponent
    ],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('100ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms', style({ opacity: 0 })),
      ]),
    ]),
  ],
  standalone: true
})
export class ResidentProfileComponent extends BaseLoadComponent<Resident> implements OnInit {

  @Input() id: number;
  payments: Payment[] = [];
  invoices: Invoice[] = [];
  service = inject(ResidentsService);
  breadCrumbItems = [];
  modalService = inject(ModalService);

  public donutChartOptions: any;

  activeTab: string = 'services';
  activeServiceTab: string = 'cold-water';

  connectedServiceData = [
    {
      service: 'BUILD_DASHBOARD.HEATING',
      colors: ['#801130', '#00000000'],
      icon: 'ri-fire-line',
      iconsColor: '#801130',
      lastReading: '156.000',
      balance: '1 250.00',
      paymentDate: '24/12/2024',
      status: 'Active',
      statusColor: 'bg-primary',
      type: UnitTypes.HEATING
    },
    {
      service: 'BUILD_DASHBOARD.COLD_WATER',
      colors: ['#5785fc','#00000000'],
      icon: 'ri-drop-line',
      iconsColor: '#5785fc',
      lastReading: '586.000',
      balance: '7 150.00',
      paymentDate: '15/12/2024',
      status: 'Active',
      statusColor: 'bg-primary',
      type: UnitTypes.COLD_WATER
    },
    {
      service: 'BUILD_DASHBOARD.HOT_WATER',
      colors: ['#b69d16','#00000000'],
      icon: 'ri-drop-line',
      iconsColor: '#b69d16',
      lastReading: '289.000',
      balance: '150.00',
      paymentDate: '05/01/2025',
      status: 'Active',
      statusColor: 'bg-primary',
      type: UnitTypes.WARM_WATER
    },
  ]

  afterLoadData(data: Resident) {
    this.id = data.id;
    super.afterLoadData(data);
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'MENU.SETTINGS' },
      { label: this.service.title, link: Resources.Residents },
      { label: this.id, active: true }
    ];
    super.ngOnInit();

    this.initializeDonutChartOptions('["--in-primary", "--in-light"]');
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  setActiveServiceTab(tab: string): void {
    this.activeServiceTab = tab;
  }

  private initializeDonutChartOptions(color: string): void {
    this.donutChartOptions = {
      series: [75, 25],
      chart: {
        type: 'donut',
        height: 60,
        width: 60
      },
      legend: {
        show: false
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '80%',
          },
        },
      }
    };
  }

  getData(): Observable<Resident> {
    return this.service.getOne(this.id);
  }

  onEdit() {
    this.modalService.open({
      component: this.service.formComponent,
      title: [this.translateService.instant('EDIT')].concat(
        this.service.titleSingular ? [this.service.titleSingular] : []
      ).join(' '),
      componentOptions: {
        selectedRow: this.data(),
        isEdit: true,
        _defaultService: this.service
      },
      modalOptions: this.service?.formModalOptions ?? {}
    }).then((res) => {
      if (res == ModalClose.SUCCESS) {
        this.loadData();
      }
    }).catch((err) => {})
  }

  createContract(type: UnitTypes) {
    this.modalService.open({
      component: ContractComponent,
      title: 'Create Contract',
      componentOptions: {
        type,
        residentId: this.id,
      },
      modalOptions: {
        size: 'lg',
        scrollable: false
      }
    })
  }
}
